import React, { useState, useEffect, useRef, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { startTranslation, stopTranslation } from './i18n/translator';

import HomePage from './pages/HomePage';
import MessagesPage from './pages/MessagesPage';
import AuthPage from './pages/AuthPage';
import DailyTournamentsPage from './pages/DailyTournamentsPage';
import KnockoutTournamentsPage from './pages/KnockoutTournamentsPage';
import RankingsPage from './pages/RankingsPage';
import LevelsPage from './pages/LevelsPage';
import PointsSystemPage from './pages/PointsSystemPage';
import ContactPage from './pages/ContactPage';
import ProfilePage from './pages/ProfilePage';
import TournamentDetailsPage from './pages/TournamentDetailsPage';
import EditProfilePage from './pages/EditProfilePage';
import AdminDashboard from './pages/AdminDashboard';
import AdminCheckPage from './pages/AdminCheckPage';
import AdminTournamentsPage from './pages/AdminTournamentsPage';
import AdminResultsPage from './pages/AdminResultsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminControlPanel from './pages/AdminControlPanel';
import AdminCancellationsPage from './pages/AdminCancellationsPage';
import TestAdminPage from './pages/TestAdminPage';
import RoeyAdminPage from './pages/RoeyAdminPage';
import CreateTournamentPage from './pages/CreateTournamentPage';
import TrainingCampsPage from './pages/TrainingCampsPage';
import WhatsAppCommunityPage from './pages/WhatsAppCommunityPage';
import PartnerBoardPage from './pages/PartnerBoardPage';
import LeaderboardsPage from './pages/LeaderboardsPage';
import FAQPage from './pages/FAQPage';
import AdminStatsPage from './pages/AdminStatsPage';
import GearBoardPage from './pages/GearBoardPage';
import CampRegistrationForm from './pages/CampRegistrationForm';
import WhatsAppButton from './components/WhatsAppButton';
import InstallPrompt from './components/InstallPrompt';
import FloatingMessenger from './components/FloatingMessenger';
import { AuthPrompts } from './components/AuthPrompts';
import CoachesPage from './pages/CoachesPage';
import CoachProfilePage from './pages/CoachProfilePage';
import CoachRegistrationPage from './pages/CoachRegistrationPage';
import AdminCoachesPage from './pages/AdminCoachesPage';
import BracketPreviewPage from './pages/BracketPreviewPage';
import GroupsPreviewPage from './pages/GroupsPreviewPage';
import TournamentsPreviewPage from './pages/TournamentsPreviewPage';
import AuthCallback from './pages/AuthCallback';
import AdminAdvancedTools from './pages/AdminAdvancedTools';
import ProfileCompletionGate from './components/ProfileCompletionGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [lang] = useState(() => localStorage.getItem('lang') || 'he');
  const isLoggingOutRef = useRef(false);
  const interceptorIdRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    if (lang === 'en') {
      root.setAttribute('dir', 'ltr');
      root.setAttribute('lang', 'en');
      document.body && document.body.classList.add('lang-en');
      startTranslation();
    } else {
      root.setAttribute('dir', 'rtl');
      root.setAttribute('lang', 'he');
      document.body && document.body.classList.remove('lang-en');
      stopTranslation();
    }
  }, [lang]);

  const toggleLang = () => {
    const next = lang === 'en' ? 'he' : 'en';
    localStorage.setItem('lang', next);
    window.location.reload();
  };

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  // Setup interceptor once, inside the component so it can access setUser
  useEffect(() => {
    // Remove previous interceptor if exists (hot reload safety)
    if (interceptorIdRef.current !== null) {
      axios.interceptors.response.eject(interceptorIdRef.current);
    }

    interceptorIdRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Network errors - don't touch auth state
        if (!error.response) {
          return Promise.reject(error);
        }

        if (error.response.status === 401) {
          const requestUrl = error.config?.url || '';
          const errorDetail = error.response?.data?.detail || '';

          // Only logout on explicit token issues from /auth/me
          if (requestUrl.includes('/auth/me') &&
              (errorDetail.includes('expired') || errorDetail.includes('Invalid') || errorDetail.includes('Could not validate'))) {
            if (!isLoggingOutRef.current) {
              isLoggingOutRef.current = true;
              localStorage.removeItem('token');
              localStorage.removeItem('rememberMe');
              sessionStorage.removeItem('token');
              delete axios.defaults.headers.common['Authorization'];
              setUser(null);
              setSessionExpired(true);
              setTimeout(() => { isLoggingOutRef.current = false; }, 3000);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.response.eject(interceptorIdRef.current);
      }
    };
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    try {
      const response = await axios.get(`${API}/auth/me`, { timeout: 10000 });
      setUser(response.data);
      const refreshed = response.headers['x-refreshed-token'];
      if (refreshed) {
        if (localStorage.getItem('token')) localStorage.setItem('token', refreshed);
        else sessionStorage.setItem('token', refreshed);
        axios.defaults.headers.common['Authorization'] = `Bearer ${refreshed}`;
      }
    } catch (error) {
      const is401 = error.response?.status === 401;
      if (is401) {
        // Token is invalid/expired - clear it
        localStorage.removeItem('token');
        localStorage.removeItem('rememberMe');
        sessionStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setSessionExpired(true);
      } else {
        // Network/server error - keep the token, user can retry
        console.warn('Auth check failed (network/server), keeping session');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = (token, userData, rememberMe = true) => {
    if (rememberMe) {
      localStorage.setItem('token', token);
      localStorage.setItem('rememberMe', 'true');
    } else {
      sessionStorage.setItem('token', token);
      localStorage.removeItem('token');
      localStorage.removeItem('rememberMe');
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    toast.success('התחברת בהצלחה!');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('התנתקת בהצלחה');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-green-50">
        <div className="text-2xl font-medium text-gray-700">טוען...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, API, theme, toggleTheme, lang, toggleLang, sessionExpired, clearSessionExpired: () => setSessionExpired(false) }}>
      <WebSocketProvider>
        <div className="App" dir="rtl">
          <BrowserRouter>
            <ProfileCompletionGate>
              <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/bracket-preview" element={<BracketPreviewPage />} />
              <Route path="/groups-preview" element={<GroupsPreviewPage />} />
              <Route path="/tournaments-preview" element={<TournamentsPreviewPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/admin/advanced" element={<AdminAdvancedTools />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/tournaments/daily" element={<DailyTournamentsPage />} />
              <Route path="/tournaments/knockout" element={<KnockoutTournamentsPage />} />
              <Route path="/tournament/:id" element={<TournamentDetailsPage />} />
              <Route path="/rankings" element={<RankingsPage />} />
              <Route path="/leaderboards" element={<LeaderboardsPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/admin/stats" element={<AdminStatsPage />} />
              <Route path="/levels" element={<LevelsPage />} />
              <Route path="/points-system" element={<PointsSystemPage />} />
              <Route path="/training-camps" element={<TrainingCampsPage />} />
              <Route path="/camp-registration" element={<CampRegistrationForm />} />
              <Route path="/camp-registration/:campId" element={<CampRegistrationForm />} />
              <Route path="/whatsapp-community" element={<WhatsAppCommunityPage />} />
              <Route path="/partner-board" element={<PartnerBoardPage />} />
              <Route path="/gear-board" element={<GearBoardPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/profile/edit" element={<EditProfilePage />} />
              <Route path="/profile/:userId" element={<ProfilePage />} />
              <Route path="/admin-check" element={<AdminCheckPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/control" element={<AdminControlPanel />} />
              <Route path="/test-admin" element={<TestAdminPage />} />
              <Route path="/roey" element={<RoeyAdminPage />} />
              <Route path="/create-tournament" element={<CreateTournamentPage />} />
              <Route path="/admin/tournaments" element={<AdminTournamentsPage />} />
              <Route path="/admin/results/:tournamentId" element={<AdminResultsPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/cancellations" element={<AdminCancellationsPage />} />
              <Route path="/coaches" element={<CoachesPage />} />
              <Route path="/coaches/register" element={<CoachRegistrationPage />} />
              <Route path="/coaches/:coachId" element={<CoachProfilePage />} />
              <Route path="/admin/coaches" element={<AdminCoachesPage />} />
            </Routes>
            <WhatsAppButton />
            <FloatingMessenger />
            <InstallPrompt />
            <AuthPrompts />
            </ProfileCompletionGate>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </div>
      </WebSocketProvider>
    </AuthContext.Provider>
  );
}

export default App;
